import { autoinject, bindable, computedFrom, observable, TaskQueue } from 'aurelia-framework';
import { DOM } from 'aurelia-pal';
import { includes, last, noop, reject } from 'lodash';
import { IDroppableHandler } from '../../components/drag-and-drop/models';
import { NotificationType, ResourcedNotificationService } from '../../components/notifications/notification-service';
import { ResponseHandler } from '../../utils/response-handler';
import { AccountingRule, AccountingRuleFolder, IAccountingRule, IAccountingRuleFolder, RuleTemplate } from './accounting-rule-models';
import { AccountingRuleApi } from "./accounting-rule-api";
import { AppSettings } from '../../utils/appsettings';

@autoinject
export class AccountingRuleList implements IDroppableHandler {
	@bindable scopeId: string;
	@bindable scopeIsClosed: boolean;
	@bindable environmentName: string;
	@bindable accountingRules: AccountingRule[];
	@bindable accountingRuleFolders: AccountingRuleFolder[];

	@bindable ruleDeleted: (rule: any) => void;
	@bindable refreshFolders: () => void;

	@observable typeFilter: string;
	@observable allCostCentersFilter: boolean;
	@observable lastModifiedRuleId: string | undefined;
	
	showOnlyErroneous = false;
	droppableHandler: IDroppableHandler;
	fetchingDatainProgress = true;
	costCenterFilter: string;
	contentFilter: string;
	showingNewInlineRuleFormFor: string;
	countOfErroneousRules: number;
	ruleCreatedTimeout: NodeJS.Timeout;

	get isDragged() {
		return this.accountingRules && this.accountingRules.some(item => item.isDragged === true);
	}
	
	@computedFrom('environmentName')
	get showRuleOrderNumber(): boolean {
		return this.environmentName === 'Production-Helsinki';
	}

	@computedFrom('showOnlyErroneous', 'costCenterFilter', 'allCostCentersFilter', 'typeFilter', 'contentFilter')
	get hasFiltersSet(): boolean {
		return this.showOnlyErroneous ||
			(!!this.costCenterFilter && !!this.costCenterFilter.length) ||
			this.allCostCentersFilter ||
			!!this.typeFilter ||
			(!!this.contentFilter && !!this.contentFilter.length);
	}

	constructor(
		private api: AccountingRuleApi,
		private responseHandler: ResponseHandler,
		private notificationService: ResourcedNotificationService,
		private taskQueue: TaskQueue) {

		this.droppableHandler = this;
	}

	get userHasReadOnlyAccess(): boolean {
		return !AppSettings.roles.some(r => r === 'GlobalAdministrator' || r === 'GlobalCustomerAdministrator');
	}

	refreshErrorCount() {
		this.countOfErroneousRules = this.accountingRules
			? this.accountingRules.filter(rule => rule.validationErrors && rule.validationErrors.length > 0).length
			: 0;
	}

	showErroneousRules() {
		this.showOnlyErroneous = true;
		this.allCostCentersFilter = null;
		this.typeFilter = null;
		this.applyFilters();
	}

	toggleFilterErroneous() {
		this.showOnlyErroneous = !this.showOnlyErroneous;
		this.applyFilters();
	}
	
	toggleRule(rule: AccountingRule) {
		rule.expanded = !rule.expanded;
		return true;
	}

	openAllRules() {
		const ruleElements = document.querySelectorAll(".rule-details")
		if (!ruleElements || ruleElements.length === 0) {
			return;
		}
		
		ruleElements.forEach(ruleElement => ruleElement.setAttribute('open', 'true'));
		this.accountingRules.filter(i => i.isVisible = true).forEach(i => i.expanded = true);
	}

	closeAllRules() {
		const ruleElements = document.querySelectorAll(".rule-details")
		if (!ruleElements || ruleElements.length === 0) {
			return;
		}
		
		ruleElements.forEach(ruleElement => ruleElement.removeAttribute('open'));
		this.accountingRules.forEach(i => i.expanded = false);
	}

	openAllFolders() {
		const folders = document.querySelectorAll('.rule-folder-details');
		if (!folders || folders.length === 0) {
			return;
		}
		
		folders.forEach(folder => folder.setAttribute('open', 'true'));
	}

	closeAllFolders() {
		const folders = document.querySelectorAll('details');
		if (!folders || folders.length === 0) {
			return;
		}
		
		folders.forEach(folder => folder.removeAttribute('open'));
	}

	inlineRuleCreated(addedRule: AccountingRule) {

		const newRule = AccountingRule.createNew(addedRule);
		newRule.expanded = true;
		newRule.isVisible = true;

		this.insertToAccountingRulesWithOrderNumberAndFolder(newRule);
		this.refreshFolders();
		this.showingNewInlineRuleFormFor = null;

		// Allow time for the new rule to be rendered before trying to open it
		this.ruleCreatedTimeout = setTimeout(() => {
			const ruleContainer = document.querySelector(`#rule-${newRule.id}`);
			if (ruleContainer) {
				ruleContainer.setAttribute('open', 'true');
			}
		}, 5);
	}

	showNewInlineRuleFormFor(rule: AccountingRule) {
		this.showingNewInlineRuleFormFor = rule ? rule.id : null;
	}

	resetFilters() {
		this.costCenterFilter = null;
		this.allCostCentersFilter = false;
		this.typeFilter = null;
		this.showOnlyErroneous = false;
		this.contentFilter = null;

		this.accountingRules.map(item => item.isVisible = true);
	}

	applyFilters() {
		this.accountingRules.map(item => item.isVisible = true);

		const notVisible = reject(this.accountingRules, item => {

			if (this.showOnlyErroneous && (!item.validationErrors || item.validationErrors.length == 0)) {
				return false;
			}

			if (this.allCostCentersFilter && !item.isForAllCostCenters) {
				return false;
			}

			if (this.typeFilter && item.typeName !== this.typeFilter) {
				return false;
			}

			if (this.costCenterFilter && this.costCenterFilter.length) {
				const filter = this.costCenterFilter.toLowerCase();
				if (!item.isForAllCostCenters &&
					!item.costCenters.find(cc => (cc.identifier && includes(cc.identifier.toLowerCase(), filter)) || (!!cc.name && includes(cc.name.toLowerCase(), filter)))) {
					return false;
				}
			}

			if (this.contentFilter && this.contentFilter.length) {
				const filter = this.contentFilter.toLowerCase();
				if (!item.definition.includes(filter)) {
					return false;
				}
			}

			return true;
		});

		notVisible.forEach(item => item.isVisible = false);
	}

	costCenterFilterKeyPressed(event: KeyboardEvent) {
		if (event.keyCode === 13) { // Listen Enter keypress
			this.applyFilters();
		}
		return true;
	}

	allCostCentersFilterChanged(newValue: boolean) {
		if (newValue == null) {
			return;
		}

		if (newValue === true) {
			this.costCenterFilter = '';
		}

		this.applyFilters();
	}

	typeFilterChanged() {
		this.applyFilters();
	}

	contentFilterKeyPressed(event: KeyboardEvent) {
		if (event.keyCode === 13) { // Listen Enter keypress
			this.applyFilters();
		}
		return true;
	}

	async refreshAccountingRules() {
		const accountingRules = await this.api.getAccountingRulesForScope(this.scopeId);

		this.accountingRules = accountingRules.map(AccountingRule.createNew);
		this.fetchingDatainProgress = false;
		this.refreshErrorCount();

		if (location.hash && location.hash.startsWith('#rule-')) {
			const selectedRule = this.accountingRules.find(r => r.id === location.hash.substr(6));
			if (selectedRule) {

				selectedRule.expanded = true;

				const elementId = location.hash.substr(1);
				this.taskQueue.queueTask(() => {
					const element = DOM.getElementById(elementId) as HTMLElement;

					window.scrollTo(0, element.parentElement.offsetTop);
				});
			}
		}
	}

	async attached() {
		if (!this.accountingRules) {
			this.accountingRules = [];
		}

		await this.refreshAccountingRules();
		this.refreshFolders();
	}
	
	detached(){
		clearTimeout(this.ruleCreatedTimeout);
	}

	updateAccountRuleClicked(rule: AccountingRule) {
		if (this.scopeIsClosed) {
			this.notificationService.push('ViewMessage_OperationNotAllowed', NotificationType.Error, null, ['kausi on suljettu'], null);
			return; 
		}

		rule.editInProgress = true;
		rule.validationErrors = null;
		rule.definition = AccountingRule.formatDefinition(rule.definition);

		this.api.updateRule(rule).then(response => {
			if (this.responseHandler.isSuccess(response.status)) {
				
				rule.expanded = false;
				const ruleContainer = document.querySelector(`#rule-${rule.id}`);
				if (ruleContainer) {
					ruleContainer.removeAttribute('open');
				}
				
				this.refreshErrorCount();
				this.notificationService.push('ViewMessage_SaveSucceeded', NotificationType.Success);
				response.json().then(updatedRule => rule.copyDataFrom(updatedRule));
				this.lastModifiedRuleId = rule.id;
			}
			else if (this.responseHandler.isClientError(response.status)) {
				response.json().then(errors => rule.validationErrors = errors);
			}

			rule.editInProgress = false;
		});

		rule.isDirty = false;
	}

	removeAccountingRule(rule: AccountingRule) {
		if (this.scopeIsClosed) {
			this.notificationService.push('ViewMessage_OperationNotAllowed', NotificationType.Error, null, ['kausi on suljettu'], null);
			return; 
		}

		this.api.removeAccountingRule(rule).then(() => {
			const index = this.accountingRules.indexOf(rule);
			this.accountingRules.splice(index, 1);
			this.refreshFolders();

			(this.ruleDeleted || noop)({ rule });
			this.refreshErrorCount();
		});
	}

	onDragEnter(event: any, model: AccountingRule) {
		if (event.preventDefault) {
			event.preventDefault(); // Necessary. Allows us to drop.
		}

		event.dataTransfer.dropEffect = 'move';
		event.target.parentNode.className = `${event.target.parentNode.className} drag-enter`;

		return false;
	}

	onDragLeave(event: any, dragTarget: any): void {
		event.target.parentNode.className = event.target.parentNode.className.replace(' drag-enter', '');
	}

	onDrop(event, model: AccountingRule) {
		event.stopPropagation(); // stops the browser from redirecting.
		event.target.parentNode.className = event.target.parentNode.className.replace(' drag-enter', '');

		const jsonData = event.dataTransfer.getData('application/json');
		const data = <AccountingRule>JSON.parse(jsonData);

		if (model && model.id === data.id) {
			return false;
		}

		const rule = this.accountingRules.find(o => o.id === data.id);

		// Remove from array if rule is there already
		this.accountingRules.splice(this.accountingRules.indexOf(rule), 1);

		// Get folder id from the target element parent
		const elementFolderId = event.toElement.folderId;
		const targetFolderId = event.toElement.folderId === 'rootFolder' ? null : elementFolderId;
		const targetFolder = this.accountingRuleFolders.find(f => f.id === elementFolderId);
	
		const newOrderNumber = model
			? model.orderNumber
			: AccountingRuleFolder.getLastOrderNumber(targetFolder);

		rule.orderNumber = newOrderNumber;
		rule.folderId = targetFolderId;

		this.insertToAccountingRulesWithOrderNumberAndFolder(rule);
		this.refreshFolders();

		return false;
	}

	insertToAccountingRulesWithOrderNumberAndFolder(rule: AccountingRule) {
		this.accountingRules.push(rule);

		// Update rule order numbers for current folder
		this.accountingRules
			.filter(r => r.id !== rule.id && r.folderId === rule.folderId && r.orderNumber >= rule.orderNumber)
			.forEach(r => r.orderNumber++);			

		void this.api.updateOrderNumbers(this.accountingRules);
		void this.api.updateRuleFolder(rule);
	}

	async createAccountingRuleFolderForScope(orderNumber: number) {
		const newIndex = orderNumber + 1;
		const newFolder = {
			scopeId: this.scopeId,
			name: `Uusi kansio ${newIndex}`,
			orderNumber: newIndex
		};
		const response = await this.api.createAccountingRuleFolderForScope(newFolder, this.scopeId);
		const folder = await response.json();

		if (this.accountingRuleFolders.findIndex(r => r.orderNumber >= newIndex) < 0) {
			this.accountingRuleFolders.push(folder);
		} else {
			for (let i = newIndex; i < this.accountingRuleFolders.length; i++) {
				this.accountingRuleFolders[i].orderNumber++;
			}
			this.accountingRuleFolders.splice(newIndex, 0, folder);
		}

		this.refreshFolders();
	}

	async onFolderNameChange(folder: IAccountingRuleFolder, newName: string) {
		if (folder.isReadOnly) {
			return;
		}

		await this.api.updateAccountingRuleFolderName(folder.id, newName);
	}

	async removeAccountingRuleFolder(folder: IAccountingRuleFolder) {
		if (folder.isReadOnly) {
			return;
		}

		await this.api.removeAccountingRuleFolder(folder.id);
		
		const index = this.accountingRuleFolders.indexOf(folder);
		this.accountingRuleFolders.splice(index, 1);
	}
}
